<template>
  <div>
    <div v-if="state === 'idle'">
      <div v-if="canPayOnline">
        <label class="text-secondary small mb-1">M-Pesa number</label>
        <vue-tel-input v-model="phone" class="mb-3"></vue-tel-input>
        <button class="btn btn-primary btn-lg btn-block" :disabled="starting" @click="start">
          <span v-if="starting" class="spinner-border spinner-border-sm mr-2"></span>
          Pay {{ money(amount) }} with M-Pesa
        </button>
        <p v-if="walletCovers" class="text-secondary small text-center mt-2 mb-0">
          Your wallet balance covers this - no M-Pesa prompt needed.
        </p>
      </div>
      <div v-else>
        <button v-if="walletCovers" class="btn btn-primary btn-lg btn-block" :disabled="starting" @click="start">
          <span v-if="starting" class="spinner-border spinner-border-sm mr-2"></span>
          Pay {{ money(amount) }} from wallet
        </button>
        <div v-else class="alert alert-secondary text-center mb-0">
          <i class="bi bi-shop mr-1"></i>
          Please pay {{ money(amount) }} at the front desk.
        </div>
      </div>
    </div>

    <div v-else-if="state === 'waiting'" class="text-center py-3">
      <div class="spinner-border text-primary mb-3"></div>
      <h5 class="font-weight-bold text-dark">Check your phone</h5>
      <p class="text-secondary mb-0">Enter your M-Pesa PIN to pay {{ money(pendingAmount) }}.</p>
      <button class="btn btn-link text-secondary mt-2" @click="reset">Cancel</button>
    </div>

    <div v-if="error" class="alert alert-danger mt-3 mb-0">{{ error }}</div>
  </div>
</template>

<script>
import { money } from './memberStore'

/**
 * Pays a renewal or a new package for a member: STK push to their wallet,
 * then polls until the server has applied it. Emits `done` with the result.
 */
export default {
  name: 'MemberPayPanel',

  props: {
    memberToken:  { type: String, required: true },
    intent:       { type: Object, required: true }, // { type: 'renew', subscription_id } | { type: 'subscribe', package_id }
    amount:       { type: Number, required: true },
    wallet:       { type: Number, default: 0 },
    canPayOnline: { type: Boolean, default: false },
    defaultPhone: { type: String, default: '' },
  },

  data() {
    return {
      phone:         this.defaultPhone,
      state:         'idle',
      starting:      false,
      error:         '',
      paymentId:     null,
      pendingAmount: 0,
      timer:         null,
      polls:         0,
    }
  },

  computed: {
    walletCovers() { return this.wallet >= this.amount },
  },

  beforeDestroy() { this.stopPolling() },

  methods: {
    money,

    async start() {
      this.error    = ''
      this.starting = true
      try {
        const { data } = await axios.post(`/api/member/${this.memberToken}/pay`, {
          ...this.intent,
          phone: this.phone || null,
        })
        if (data.status === 'applied') {
          this.$emit('done', data.result)
          return
        }
        this.paymentId     = data.payment_id
        this.pendingAmount = data.amount
        this.state         = 'waiting'
        this.polls         = 0
        this.poll()
      } catch (e) {
        this.error = e.response?.data?.message || 'Payment could not start. Please try again.'
      } finally {
        this.starting = false
      }
    },

    poll() {
      this.stopPolling()
      this.timer = setTimeout(async () => {
        this.polls++
        try {
          const { data } = await axios.get(`/api/member/${this.memberToken}/payments/${this.paymentId}`, { _silent: true })
          if (data.status === 'applied') { this.state = 'idle'; this.$emit('done', data.result); return }
          if (data.status === 'paid')    { this.state = 'idle'; this.$emit('done', null); return }
          if (data.status === 'failed') {
            this.state = 'idle'
            this.error = data.message || 'The M-Pesa payment did not go through.'
            return
          }
        } catch (e) {
          // keep polling through transient errors
        }
        if (this.polls < 40) {
          this.poll()
        } else {
          this.state = 'idle'
          this.error = "We haven't received the payment yet. If you paid, it will reflect shortly in your wallet."
        }
      }, 3000)
    },

    stopPolling() {
      if (this.timer) clearTimeout(this.timer)
      this.timer = null
    },

    reset() {
      this.stopPolling()
      this.state = 'idle'
    },
  },
}
</script>
